// The Review Center's Documentation tab: everything the documentation
// pipeline and the technical views are waiting on a person for - document
// details Claude proposed, drawings that could not be placed, unmapped
// layers, traced pathways to check, which drawing gave a level its rooms,
// flags and explanations on views, quantities that disagree with the model,
// schedule activities mapped to the building, and tracked elements a view's
// regeneration no longer finds (their history is kept; a person says they are
// gone or which new row they became).
//
// Each row is read from the record that owns it, and a decision is written
// back to that record (utils/glance/reviewQueue), so nothing is reviewed in
// two places. The Technical documentation page shows only a count and a link
// here.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Modal, Select, Skeleton, Space, Switch, Table, Tag, Tooltip, message } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, UndoOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { openOverlay } from "../views/ProcessingPanel";
import VersionChip from "components/Documents/VersionChip";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

// When the decisions reach the building data (utils/glance/followUp): a
// decision that changes it is applied by one run a few minutes after the
// last one - said here, with the time, and which run applied it.
const hhmm = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
export function followUpText(f) {
  if (!f) return "";
  const what = (n, text) => (text ? `${text}` : `${n} change${n === 1 ? "" : "s"}`);
  if (f.status === "waiting") return `The building data will be updated at ${hhmm(f.dueAt)} with ${what(f.decisions, f.sourcesText)}.`;
  if (f.status === "queued") return `Updating the building data now with ${what(f.runDecisions || f.decisions, f.runSourcesText)} (run ${String(f.runId).slice(-6)})${f.decisions > (f.runDecisions || 0) ? `; ${f.decisions - (f.runDecisions || 0)} more after it` : ""}.`;
  if (f.status === "applied") return `The building data was updated at ${hhmm(f.appliedAt)} (run ${String(f.runId).slice(-6)}); what changes its levels, grid or zones waits for you in this list.`;
  if (f.status === "failed") return `The last update did not finish: ${f.error}. Nothing is lost; the next update carries it.`;
  return "";
}
export function FollowUpNote({ projectId, tick }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    const read = () => fetch(`${url}/projects/${projectId}/glance/review/follow-up`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null)).then((b) => { if (alive) setF(b && b.followUp); }).catch(() => {});
    read();
    const t = setInterval(read, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [projectId, tick]);
  const text = followUpText(f);
  return text ? <div role="status" style={{ margin: "6px 0 0", color: f.status === "failed" ? "#c62828" : "#1F6FD1" }}>{text}</div> : null;
}

async function api(projectId, path, { method = "GET", body } = {}) {
  const r = await fetch(`${url}/projects/${projectId}/glance${path}`, {
    method, headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...auth() }, body: body ? JSON.stringify(body) : undefined,
  });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.err || `HTTP ${r.status}`);
  return out;
}

const LEVELS = ["", "L1", "L2", "L3", "L4", "L5", "L6", "roof"];
const DOC_TYPES = ["", "floor-plan", "reflected-ceiling-plan", "roof-plan", "underfloor-plan", "enlarged-plan", "riser-diagram",
  "panel-schedule", "equipment-schedule", "specification", "energy-report", "keynotes", "details", "sections", "model", "project-schedule", "look-ahead-schedule", "install-tracker", "report", "other"];

function EditDetails({ row, onCancel, onSave }) {
  const [draft, setDraft] = useState(row ? row.proposal : {});
  useEffect(() => { if (row) setDraft(row.proposal); }, [row]);
  if (!row) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <Modal open title={`Correct: ${row.title}`} okText="Save as corrected" onCancel={onCancel} onOk={() => onSave(draft)}>
      <p style={{ color: "#666" }}>What you save here replaces the automatic proposal, is marked as yours, and is kept when the documents are processed again.</p>
      <Space direction="vertical" style={{ width: "100%" }}>
        <label htmlFor="rev-discipline">Discipline<Input id="rev-discipline" value={draft.discipline} onChange={(e) => set("discipline", e.target.value)} /></label>
        <label htmlFor="rev-doctype">Document type
          <Select id="rev-doctype" style={{ width: "100%" }} value={draft.docType} onChange={(v) => set("docType", v)} options={DOC_TYPES.map((t) => ({ value: t, label: t || "none" }))} />
        </label>
        <label htmlFor="rev-level">Level
          <Select id="rev-level" style={{ width: "100%" }} value={draft.level} onChange={(v) => set("level", v)} options={LEVELS.map((t) => ({ value: t, label: t || "none" }))} />
        </label>
        <label htmlFor="rev-area">Area<Input id="rev-area" maxLength={1} value={draft.area} onChange={(e) => set("area", e.target.value.toUpperCase())} /></label>
      </Space>
    </Modal>
  );
}

export default function DocumentationReview({ projectId, filter = "", reloadKey = 0, onDecided }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  // Decided document details: nothing is waiting on them, but an answer given
  // in a hurry must still be correctable (or re-opened for the next run).
  const [decided, setDecided] = useState(false);
  const [decidedTick, setDecidedTick] = useState(0);

  const load = useCallback(() => {
    if (!projectId) { setRows([]); return; }
    setError("");
    api(projectId, decided ? "/review?decided=1" : "/review").then((o) => setRows(o.rows)).catch((e) => { setError(e.message); setRows([]); });
  }, [projectId, decided]);
  useEffect(() => { load(); setPicked([]); }, [load, reloadKey]);

  const shown = useMemo(() => {
    const q = String(filter || "").trim().toLowerCase();
    return (rows || []).filter((r) => !q || `${r.title} ${r.detail} ${r.kindLabel}`.toLowerCase().includes(q));
  }, [rows, filter]);

  const decide = async (ids, decision, fields) => {
    setBusy(true);
    try {
      const out = await api(projectId, "/review/decide", { method: "POST", body: { ids, decision, fields } });
      const word = { accept: "accepted", reject: "rejected", reopen: "re-opened" }[decision] || "corrected";
      message.success(`${out.done.length} ${word}${out.failed.length ? `; ${out.failed.length} could not be: ${out.failed[0].err}` : ""}`);
      setPicked([]);
      load();
      setDecidedTick((n) => n + 1);
      if (onDecided) onDecided();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  if (!projectId) return <Empty description="Choose a project to see what its documentation is waiting on." />;
  const toggle = (
    <div style={{ margin: "0 0 10px" }}>
      <label htmlFor="doc-decided" style={{ cursor: "pointer" }}>
        <Switch id="doc-decided" size="small" checked={decided} onChange={(v) => { setRows(null); setDecided(v); }} /> Show document details already decided
      </label>
      <FollowUpNote projectId={projectId} tick={decidedTick} />
    </div>
  );
  if (rows === null) return <div>{toggle}<Skeleton active paragraph={{ rows: 6 }} /></div>;
  if (error) return <div>{toggle}<Empty description={error} /></div>;
  if (!shown.length) {
    return <div>{toggle}<Empty description={rows.length ? "Nothing matches that search." : decided ? "No document details have been decided yet." : "Nothing is waiting on the documentation."} /></div>;
  }

  const columns = [
    { title: "What", dataIndex: "kindLabel", width: 230, render: (k, r) => (
      <span>
        {k}
        {!decided && r.by === "claude" && <Tooltip title="Proposed automatically"><Tag style={{ marginLeft: 6 }}>Pending</Tag></Tooltip>}
        {decided && <Tag style={{ marginLeft: 6 }} color={r.decided === "rejected" ? "red" : "green"}>{r.decided}{r.reviewedBy ? ` by ${r.reviewedBy}` : ""}</Tag>}
      </span>
    ) },
    { title: "About", dataIndex: "title", render: (t, r) => (
      <div>
        <div style={{ fontWeight: 500 }}>
          {r.viewId ? <a href={`/documentation/${projectId}?tab=${encodeURIComponent(r.viewId)}`} target="_blank" rel="noopener noreferrer">{t}</a> : t}
          {r.docId && <VersionChip docId={r.docId} rev={r.rev || 0} count={r.versionCount} />}
        </div>
        <div style={{ color: "#666", fontSize: 13 }}>{r.detail}{r.reason ? ` — ${r.reason}` : ""}</div>
        {r.confidence != null && <div style={{ color: "#8c8c8c", fontSize: 12 }}>confidence {Math.round(r.confidence * 100)}%</div>}
      </div>
    ) },
    { title: "", key: "act", width: 280, render: (_, r) => (
      <Space size={4} wrap>
        {(r.kind === "registration" || (r.kind === "alignment" && r.page)) && r.docId && (
          <Tooltip title="Place this sheet by hand with two points; a hand alignment always wins and carries over to later revisions">
            <Button size="small" href={`/align/${projectId}?docId=${encodeURIComponent(r.docId)}&page=${r.page || 1}`} target="_blank" rel="noopener noreferrer">Realign</Button>
          </Tooltip>
        )}
        {r.kind === "recon" && (
          <Button size="small" href={`/documentation/${projectId}?tab=processing`} target="_blank" rel="noopener noreferrer">Read the report</Button>
        )}
        {r.kind === "trace" && r.overlay && (
          <Button size="small" onClick={() => openOverlay(projectId, r.runId, r.overlay)}>Check the trace</Button>
        )}
        {r.editable && <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)}>Correct</Button>}
        {decided && r.reopenable && (
          <Tooltip title="Back to pending: it may be proposed again on the next run">
            <Button size="small" icon={<UndoOutlined />} loading={busy} onClick={() => decide([r.id], "reopen")}>Re-open</Button>
          </Tooltip>
        )}
        {!decided && (r.options || []).length > 0 && (
          <Select size="small" style={{ minWidth: 170 }} placeholder="Correct to…" aria-label={`Correct ${r.title}`} value={undefined}
                  options={r.options.filter((o) => o.value !== r.value)} onChange={(value) => decide([r.id], "edit", { value })} />
        )}
        {!decided && r.kind === "orphan" && (r.candidates || []).length > 0 && (
          <Select size="small" style={{ minWidth: 150 }} placeholder="It became…" aria-label="The row it became"
                  options={r.candidates.map((c) => ({ value: c.rowId, label: `${c.rowId}${c.distFt != null ? ` · ${c.distFt} ft away` : ""}` }))}
                  onChange={(rowId) => decide([r.id], "edit", { rowId })} />
        )}
        {!decided && <Button size="small" type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => decide([r.id], "accept")}>
          {r.kind === "rec" ? "Take model quantity" : r.kind === "orphan" ? "It is gone" : "Accept"}
        </Button>}
        {!decided && r.kind !== "orphan" && <Button size="small" danger icon={<CloseOutlined />} loading={busy} onClick={() => decide([r.id], "reject")}>
          {r.kind === "rec" ? "Keep drawings" : "Reject"}
        </Button>}
      </Space>
    ) },
  ];

  return (
    <div>
      {toggle}
      <p style={{ color: "#666", marginTop: 0 }}>
        What the documentation pipeline and the technical views are waiting on. Each decision is written back where the item lives,
        so it is never asked twice - accepting a traced page keeps it on the next run, rejecting it leaves that page out.
      </p>
      {picked.length > 0 && (
        <Space style={{ marginBottom: 10 }}>
          <Button type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => decide(picked, "accept")}>Accept selected ({picked.length})</Button>
          <Button danger icon={<CloseOutlined />} loading={busy} onClick={() => decide(picked, "reject")}>Reject selected</Button>
          <Button type="text" onClick={() => setPicked([])}>Clear</Button>
        </Space>
      )}
      <Table size="small" rowKey="id" dataSource={shown} columns={columns}
             rowSelection={decided ? undefined : { selectedRowKeys: picked, onChange: setPicked }}
             pagination={shown.length > 50 ? { pageSize: 50 } : false} />
      <EditDetails row={editing} onCancel={() => setEditing(null)}
                   onSave={(fields) => { const r = editing; setEditing(null); decide([r.id], "edit", fields); }} />
    </div>
  );
}
